import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { rupees, fmtDate, can } from "@/lib/helpers";
import { PageHeader, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { TrendUp } from "@phosphor-icons/react";

export default function GoldRates() {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [rates, setRates] = useState({ "24K": "", "22K": "", "18K": "", "Silver 999": "", "Silver 925": "" });
  const [saving, setSaving] = useState(false);
  const editable = can(user, "gold_rates", "create");

  const load = async () => {
    const [c, h] = await Promise.all([
      api.get("/gold-rates/current"),
      api.get("/gold-rates"),
    ]);
    setCurrent(c.data);
    setHistory(h.data);
    if (c.data?.rates) setRates({ ...rates, ...c.data.rates });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const clean = {};
    Object.entries(rates).forEach(([k, v]) => { if (v !== "" && v != null) clean[k] = Number(v); });
    if (Object.keys(clean).length === 0) return toast.error("Enter at least one rate");
    setSaving(true);
    try {
      await api.post("/gold-rates", { rates: clean });
      toast.success("Gold rates updated");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Gold Rate Management" subtitle="Daily rates per purity — versioned & audit-logged." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 bg-white border-[#E6E2D6] shadow-sm">
          <div className="text-sm font-semibold text-[#1A1A1A] mb-4">{editable ? "Set New Rates (per gram)" : "Current Rates (per gram)"}</div>
          <div className="space-y-3">
            {["24K", "22K", "18K", "Silver 999", "Silver 925"].map((k) => (
              <div key={k} className="flex items-center gap-3">
                <Label className="w-24 text-sm text-[#4A4A4A]">{k}</Label>
                <Input type="number" value={rates[k] ?? ""} disabled={!editable}
                  onChange={(e) => setRates({ ...rates, [k]: e.target.value })}
                  className="bg-white border-[#E6E2D6] tabular-nums" data-testid={`rate-input-${k}`} />
              </div>
            ))}
          </div>
          {editable && (
            <Button onClick={save} disabled={saving} className="w-full mt-4 bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="save-gold-rate">
              {saving ? "Saving…" : "Publish Rates"}
            </Button>
          )}
          {current && <div className="text-xs text-[#8A8A8A] mt-3">Effective: {fmtDate(current.effective_date)} · by {current.created_by_name}</div>}
        </Card>

        <Card className="lg:col-span-2 p-5 bg-white border-[#E6E2D6] shadow-sm">
          <div className="text-sm font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2"><TrendUp size={16} className="text-[#C5A059]" /> Rate History</div>
          {history.length === 0 ? <EmptyState /> : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">24K</TableHead>
                    <TableHead className="text-right">22K</TableHead>
                    <TableHead className="text-right">18K</TableHead>
                    <TableHead className="text-right">Slv 999</TableHead>
                    <TableHead className="text-right">Slv 925</TableHead>
                    <TableHead>Updated By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id} data-testid={`rate-row-${h.id}`}>
                      <TableCell className="text-sm">{fmtDate(h.effective_date)}</TableCell>
                      <TableCell className="text-right tabular-nums">{h.rates["24K"] ? rupees(h.rates["24K"]).replace(".00", "") : "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{h.rates["22K"] ? rupees(h.rates["22K"]).replace(".00", "") : "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{h.rates["18K"] ? rupees(h.rates["18K"]).replace(".00", "") : "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{h.rates["Silver 999"] ? rupees(h.rates["Silver 999"]).replace(".00", "") : "-"}</TableCell>
                      <TableCell className="text-right tabular-nums">{h.rates["Silver 925"] ? rupees(h.rates["Silver 925"]).replace(".00", "") : "-"}</TableCell>
                      <TableCell className="text-sm text-[#8A8A8A]">{h.created_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
